import { useState, useEffect, useRef } from 'react';
import { marked } from 'marked';
import mermaid from 'mermaid';

interface DocumentViewerProps {
  clientId: string;
  filename: string | null;
  attachedDocument?: string | null;
  onAttachDocument?: (filename: string | null) => void;
}

type FileType = 'markdown' | 'html' | 'pdf' | 'image' | 'text' | 'unknown';

// Initialiser mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'Inter, sans-serif',
});

export default function DocumentViewer({ clientId, filename, attachedDocument, onAttachDocument }: DocumentViewerProps) {
  const [content, setContent] = useState<string>('');
  const [rawContent, setRawContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const getFileType = (name: string): FileType => {
    const ext = name.toLowerCase().split('.').pop() || '';
    if (['md', 'markdown'].includes(ext)) return 'markdown';
    if (['html', 'htm'].includes(ext)) return 'html';
    if (ext === 'pdf') return 'pdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (['txt', 'log', 'json', 'xml', 'yaml', 'yml'].includes(ext)) return 'text';
    return 'unknown';
  };

  const getFileIcon = (type: FileType): string => {
    switch (type) {
      case 'markdown': return '📝';
      case 'html': return '🌐';
      case 'pdf': return '📕';
      case 'image': return '🖼️';
      case 'text': return '📄';
      default: return '📎';
    }
  };

  // Render Mermaid diagrams after content is loaded
  useEffect(() => {
    if (content && contentRef.current) {
      const mermaidElements = contentRef.current.querySelectorAll('.mermaid, pre code.language-mermaid');
      mermaidElements.forEach(async (el, index) => {
        try {
          const code = el.textContent || '';
          const id = `mermaid-${Date.now()}-${index}`;
          const { svg } = await mermaid.render(id, code);
          const wrapper = document.createElement('div');
          wrapper.className = 'mermaid-diagram';
          wrapper.innerHTML = svg;
          el.parentNode?.replaceChild(wrapper, el);
        } catch (err) {
          console.error('Mermaid render error:', err);
        }
      });
    }
  }, [content]);

  useEffect(() => {
    if (filename) {
      fetchDocument();
    } else {
      setContent('');
      setRawContent('');
    }
  }, [clientId, filename]);

  const fetchDocument = async () => {
    if (!filename) return;

    const fileType = getFileType(filename);

    try {
      setLoading(true);
      setError(null);

      // Pour images et PDF, on n'a pas besoin de fetch le contenu
      if (fileType === 'image' || fileType === 'pdf') {
        setContent('');
        setRawContent('');
        setLoading(false);
        return;
      }

      const encodedFilename = encodeURIComponent(filename);
      const response = await fetch(`/api/portal/clients/${clientId}/docs/${encodedFilename}`);
      if (!response.ok) throw new Error('Erreur chargement document');
      const data = await response.json();

      if (data.content) {
        setRawContent(data.content);

        if (fileType === 'markdown') {
          // Custom renderer pour mermaid
          const renderer = new marked.Renderer();
          renderer.code = function(code: string, language: string | undefined, _isEscaped: boolean): string {
            if (language === 'mermaid') {
              return `<div class="mermaid">${code}</div>`;
            }
            // Default code rendering
            const escaped = code.replace(/</g, '&lt;').replace(/>/g, '&gt;');
            return `<pre><code class="language-${language || ''}">${escaped}</code></pre>`;
          };
          marked.setOptions({ gfm: true, breaks: true });
          const htmlContent = await marked.parse(data.content, { renderer });
          setContent(htmlContent);
        } else if (fileType === 'html') {
          setContent(data.content);
        } else {
          // Text et autres: afficher brut dans un pre
          setContent(data.content);
        }
      } else {
        setContent('<p class="text-gray-400 italic">Document vide</p>');
        setRawContent('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setContent('');
      setRawContent('');
    } finally {
      setLoading(false);
    }
  };

  // Fonction d'impression - Style McKinsey A4
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const today = new Date().toLocaleDateString('fr-CH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${filename}</title>
        <style>
          @page {
            size: A4;
            margin: 20mm 15mm 25mm 15mm;
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Georgia, 'Times New Roman', serif;
            font-size: 11pt;
            line-height: 1.6;
            color: #1a1a1a;
            background: white;
          }

          /* Header de page */
          .doc-header {
            border-bottom: 2pt solid #003366;
            padding-bottom: 4mm;
            margin-bottom: 8mm;
          }
          .doc-header h1 {
            font-family: Arial, sans-serif;
            font-size: 20pt;
            font-weight: 700;
            color: #003366;
            margin: 0;
          }
          .doc-meta {
            font-family: Arial, sans-serif;
            font-size: 9pt;
            color: #666666;
            margin-top: 2mm;
          }

          /* Titres */
          h1 { font-family: Arial, sans-serif; font-size: 20pt; font-weight: 700; color: #003366; margin: 0 0 6mm 0; padding-bottom: 3mm; border-bottom: 2pt solid #003366; }
          h2 { font-family: Arial, sans-serif; font-size: 13pt; font-weight: 700; color: #003366; text-transform: uppercase; letter-spacing: 1px; margin: 8mm 0 4mm 0; padding-bottom: 2mm; border-bottom: 0.5pt solid #cccccc; counter-increment: h2counter; }
          h2::before { content: counter(h2counter) ". "; }
          h3 { font-family: Arial, sans-serif; font-size: 11pt; font-weight: 700; color: #333333; margin: 6mm 0 3mm 0; counter-increment: h3counter; }
          h3::before { content: counter(h2counter) "." counter(h3counter) " "; }
          h4 { font-family: Arial, sans-serif; font-size: 10pt; font-weight: 700; color: #555555; margin: 5mm 0 2mm 0; font-style: italic; }

          /* Contenu */
          p { margin: 0 0 3mm 0; text-align: justify; }

          /* Tableaux */
          table { width: 100%; border-collapse: collapse; margin: 5mm 0; font-family: Arial, sans-serif; font-size: 9pt; }
          thead { background: #003366; }
          th { padding: 2.5mm 3mm; text-align: left; font-weight: 700; color: white; text-transform: uppercase; font-size: 8pt; border: 0.5pt solid #003366; }
          td { padding: 2mm 3mm; border: 0.5pt solid #cccccc; vertical-align: top; }
          tr:nth-child(even) { background: #f5f5f5; }
          td:first-child { font-weight: 600; background: #fafafa; }

          /* Listes */
          ul, ol { margin: 3mm 0; padding-left: 6mm; }
          li { margin-bottom: 1.5mm; }
          li::marker { color: #003366; }

          /* Code */
          code { font-family: Consolas, Monaco, monospace; background: #f0f0f0; padding: 1px 3px; font-size: 9pt; }
          pre { background: #2d2d2d; color: #f8f8f2; padding: 3mm; font-size: 8pt; border-left: 2pt solid #003366; margin: 4mm 0; white-space: pre-wrap; page-break-inside: avoid; }
          pre code { background: transparent; color: inherit; padding: 0; }

          /* Citations */
          blockquote { border-left: 2pt solid #003366; background: #f8f9fa; padding: 3mm 4mm; margin: 4mm 0; font-style: italic; color: #555; page-break-inside: avoid; }
          blockquote p { margin: 0; text-align: left; }

          /* Images et diagrammes */
          img { max-width: 100%; max-height: 150mm; page-break-inside: avoid; }
          .mermaid-diagram { margin: 4mm 0; text-align: center; page-break-inside: avoid; }
          .mermaid-diagram svg { max-width: 100%; }

          /* Emphases */
          strong { font-weight: 700; color: #1a1a1a; }

          /* Séparateurs */
          hr { border: none; height: 0.5pt; background: #cccccc; margin: 6mm 0; }

          /* Footer */
          .doc-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            height: 15mm;
            font-family: Arial, sans-serif;
            font-size: 8pt;
            color: #666666;
            border-top: 0.5pt solid #cccccc;
            padding-top: 3mm;
            display: flex;
            justify-content: space-between;
          }

          /* Counters */
          body { counter-reset: h2counter; }
          h2 { counter-reset: h3counter; }

          /* Page breaks */
          h1, h2, h3, h4 { page-break-after: avoid; }
          table, pre, blockquote, img { page-break-inside: avoid; }
        </style>
      </head>
      <body>
        <div class="doc-header">
          <h1 style="border: none; padding: 0; margin: 0;">${filename?.replace(/\.[^/.]+$/, '').replace(/_/g, ' ')}</h1>
          <div class="doc-meta">Document généré le ${today} | Format A4 | Confidentiel</div>
        </div>
        ${contentRef.current?.innerHTML || content}
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Export PDF (via impression)
  const handleExportPDF = () => {
    handlePrint(); // Le navigateur permet de "Enregistrer en PDF" depuis le dialogue d'impression
  };

  if (!filename) {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center max-w-xs">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-white shadow-sm flex items-center justify-center">
            <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-gray-500 font-medium">Sélectionnez un document</p>
          <p className="text-gray-400 text-sm mt-1">Cliquez sur un fichier dans la liste</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Chargement du document...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-sm text-red-600 mb-3">{error}</p>
          <button
            onClick={fetchDocument}
            className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const fileType = getFileType(filename);
  const fileIcon = getFileIcon(fileType);

  // Détecter si c'est une fiche référence _PDF.md ou _Word.md → afficher le PDF embedded
  const pdfLinkMatch = rawContent.match(/\[.*?\]\((\/pdfs\/[^)]+\.(pdf|docx?))\)/i);
  const isRefFile = filename.includes('_PDF.md') || filename.includes('_Word.md');
  const embeddedPdfUrl = (isRefFile && pdfLinkMatch) ? pdfLinkMatch[1] : null;
  const embeddedDocName = filename.split('/').pop()?.replace(/_(PDF|Word)\.md$/i, '') || filename;

  // Rendu selon le type de fichier
  const renderContent = () => {
    // Fiche _PDF.md ou _Word.md → afficher le PDF embedded directement
    if (embeddedPdfUrl) {
      const isPdfFile = embeddedPdfUrl.toLowerCase().endsWith('.pdf');
      const docTypeLabel = isPdfFile ? 'PDF' : 'Word';
      return (
        <div className="h-full flex flex-col bg-gray-100">
          {/* Info bar */}
          <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-sm">📄</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{embeddedDocName}</p>
              <p className="text-[11px] text-gray-400">Document {docTypeLabel}{filename.includes('_Word.md') ? ' (converti)' : ''}</p>
            </div>
            <a
              href={embeddedPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-medium rounded-lg transition-colors flex-shrink-0"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Ouvrir
            </a>
          </div>
          <iframe
            src={embeddedPdfUrl}
            className="flex-1 w-full border-0"
            title={embeddedDocName}
          />
        </div>
      );
    }

    switch (fileType) {
      case 'markdown':
        return (
          <article
            ref={contentRef}
            className="audit-document w-full h-full px-8 py-8"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        );

      case 'html':
        return (
          <iframe
            srcDoc={content}
            className="w-full h-full border-0"
            title={filename}
            sandbox="allow-same-origin"
          />
        );

      case 'pdf':
        return (
          <div className="h-full flex flex-col bg-gray-100">
            <iframe
              src={`/api/portal/clients/${clientId}/docs/${encodeURIComponent(filename)}?download=true`}
              className="flex-1 w-full border-0"
              title={filename}
            />
            <div className="flex items-center justify-center gap-3 py-2 bg-white border-t border-gray-200">
              <span className="text-xs text-gray-400">{filename}</span>
              <a
                href={`/api/portal/clients/${clientId}/docs/${encodeURIComponent(filename)}?download=true`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-500 hover:bg-indigo-600 text-white text-xs rounded-lg transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Ouvrir
              </a>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="h-full flex items-center justify-center bg-gray-100 p-8">
            <div className="text-center">
              <div className="text-6xl mb-4">🖼️</div>
              <p className="text-gray-600 font-medium mb-2">Image</p>
              <p className="text-gray-400 text-sm">{filename}</p>
              <p className="text-gray-400 text-xs mt-4">
                Prévisualisation non disponible
              </p>
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="w-full h-full p-6">
            <pre className="bg-gray-900 text-gray-100 p-6 rounded-xl overflow-x-auto text-sm leading-relaxed font-mono">
              {content}
            </pre>
          </div>
        );

      default:
        return (
          <div className="h-full flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <div className="text-6xl mb-4">📎</div>
              <p className="text-gray-600 font-medium mb-2">Format non supporté</p>
              <p className="text-gray-400 text-sm">{filename}</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Document Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <span className="text-lg">{fileIcon}</span>
            </div>
            <div>
              <h2 className="font-semibold text-gray-800 truncate max-w-md">
                {filename.replace(/\.[^/.]+$/, '')}
              </h2>
              <p className="text-xs text-gray-400">{filename}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full uppercase">
              {fileType}
            </span>

            {/* Bouton @ - Attacher au chatbot */}
            {onAttachDocument && (
              <button
                onClick={() => onAttachDocument(attachedDocument === filename ? null : filename)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold rounded-lg transition-all ${
                  attachedDocument === filename
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                    : 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                }`}
                title={attachedDocument === filename ? 'Détacher du chatbot' : 'Attacher au chatbot'}
              >
                <span className="text-base">@</span>
                {attachedDocument === filename ? 'Attaché' : 'IA'}
              </button>
            )}

            {/* Bouton Imprimer */}
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors"
              title="Imprimer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Imprimer
            </button>

            {/* Bouton PDF */}
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 rounded-lg transition-colors"
              title="Exporter en PDF"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              PDF
            </button>

            <button
              onClick={fetchDocument}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Rafraîchir"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Document Content */}
      <div className="flex-1 overflow-y-auto">
        {renderContent()}
      </div>
    </div>
  );
}
